import dayjs from 'dayjs';
import { t } from '../i18n';

export const formatDescriptionProperty = (description, format) => {
    switch (format) {
        case 'year':
            return dayjs(description).format('YYYY');
        case 'date':
            return dayjs(description).format('YYYY-MM-DD');
        default:
            return description;
    }
};

export const formatValue = (value) => {
    const num = Number(value);
    if (num > 9999) {
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    }
    return value;
};

const VALUE_FORMATS = {
    thousands: {
        divisor: 1000,
        suffixKey: 'numberSuffixes.thousand',
    },
    millions: {
        divisor: 1000000,
        suffixKey: 'numberSuffixes.million',
    },
    billions: {
        divisor: 1000000000,
        suffixKey: 'numberSuffixes.billion',
    },
    trillions: {
        divisor: 1000000000000,
        suffixKey: 'numberSuffixes.trillion',
    },
};

export const isValueScaleFormat = (valueFormat) =>
    Object.prototype.hasOwnProperty.call(VALUE_FORMATS, valueFormat);

export const formatValueWithScale = (
    value,
    valueFormat,
    decimalPrecision = 2
) => {
    const format = VALUE_FORMATS[valueFormat];
    const num = Number(value);

    if (!format) {
        return {
            value: formatValue(value),
            unit: null,
        };
    }

    if (Number.isNaN(num)) {
        return {
            value: formatValue(value),
            unit: t(format.suffixKey),
        };
    }

    return {
        value: parseFloat((num / format.divisor).toFixed(decimalPrecision)),
        unit: t(format.suffixKey),
    };
};

export const formatCompactNumber = (number) => {
    const num = Number(number);

    if (Number.isNaN(num)) {
        return number;
    }

    const units = [
        { divisor: 1e9, suffix: 'bn' },
        { divisor: 1e6, suffix: 'm' },
        { divisor: 1e3, suffix: 'k' },
    ];

    const unit = units.find(({ divisor }) => Math.abs(num) >= divisor);
    if (!unit) {
        return String(num);
    }

    const scaled = num / unit.divisor;
    const rounded = scaled >= 100 ? Math.round(scaled) : +scaled.toFixed(1);

    return `${rounded}${unit.suffix}`;
};

export const formatNumberWithSuffix = (number, decimalPrecision = 2) => {
    const num = Number(number);

    if (num < 1000000) {
        return {
            value: num.toFixed(decimalPrecision),
            suffix: '',
        };
    }

    const suffixes = [
        '',
        t('numberSuffixes.million'),
        t('numberSuffixes.billion'),
        t('numberSuffixes.trillion'),
        t('numberSuffixes.quadrillion'),
    ];

    let unitIndex = 0;

    let absNum = Math.abs(num);

    while (absNum >= 1000000 && unitIndex < suffixes.length - 1) {
        absNum /= 1000;
        unitIndex++;
    }

    const scaledNum =
        Math.sign(num) * parseFloat((absNum / 1000).toFixed(decimalPrecision));

    return {
        value: scaledNum,
        suffix: suffixes[unitIndex],
    };
};

export const formatNumberWithSuffixAsString = (
    number,
    decimalPrecision = 2
) => {
    const num = formatNumberWithSuffix(number, decimalPrecision);

    if (!num.suffix) {
        // Format with US-style thousands separator
        const formatted = Number(number).toLocaleString('en-US', {
            minimumFractionDigits: decimalPrecision,
            maximumFractionDigits: decimalPrecision,
        });
        return formatted;
    }
    return `${num.value} ${num.suffix}`;
};

export const formatAuthorNames = (names) => {
    if (!names.length) {
        return '';
    } else if (names.length === 1) {
        return names[0];
    } else if (names.length === 2) {
        return `${names[0]} ${t('general.and')} ${names[1]}`;
    } else {
        const lastAuthor = names.pop();
        return `${names.join(', ')} ${t('general.and')} ${lastAuthor}`;
    }
};

export const formatAuthorRoles = (names) => {
    names = names.map((role) => role || t('general.na'));

    names = names.map((role, index) => {
        if (index === 0) {
            return role
                ? role.charAt(0).toUpperCase() + role.slice(1).toLowerCase()
                : '';
        }
        return role.toLowerCase();
    });

    if (names.length === 1) {
        return names[0];
    } else if (names.length === 2) {
        return `${names[0]} ${t('general.and')} ${names[1]}`;
    } else {
        const lastAuthor = names.pop();
        return `${names.join(', ')} ${t('general.and')} ${lastAuthor}`;
    }
};

export const getFirstUrlPathSegment = (url) => {
    const urlObject = new URL(url);
    const pathSegments = urlObject.pathname
        .split('/')
        .filter((segment) => segment);
    return pathSegments.length ? `/${pathSegments[0]}` : '';
};
